import { useMemo } from 'react';
import { Set } from 'api/models/Sets';
import useSadlSet from './useSadlSet';
import useSetDisplayLabel from 'hooks/useSetDisplayLabel';

export type ManualRow = {
  id: string;
  type: 'Set' | 'Device';
  code: string;
  name: string;
  path: string;
};

type Params = {
  deliverySet?: Set;
  supplierTenantName?: string;
};

export const useSetManuals = ({ deliverySet, supplierTenantName }: Params): ManualRow[] => {
  const sadlSet = useSadlSet({ deliverySet, supplierTenantName });

  const setLabel = useSetDisplayLabel(deliverySet);

  const manualRows = useMemo<ManualRow[]>(() => {
    const rows: ManualRow[] = [];
    if (!sadlSet) return rows;

    // 1. Add the main Set Manual
    const setManualPath = sadlSet.set_manuals?.manual_english || sadlSet.set_manuals?.manual_japanese;
    if (setManualPath) {
      rows.push({
        id: 'set-manual',
        type: 'Set',
        code: sadlSet.display_code || sadlSet.code || '',
        name: setLabel,
        path: setManualPath,
      });
    }

    // 2. Iterate through trays and parts to find Device Manuals
    Object.values(sadlSet.trays).forEach(tray => {
      if (!tray.parts) return;

      Object.entries(tray.parts).forEach(([partCode, partDetails]) => {
        const deviceManualPath =
          partDetails.language_metadata?.manual_english || partDetails.language_metadata?.manual_japanese;

        if (deviceManualPath) {
          rows.push({
            id: partCode,
            type: 'Device',
            code: partCode,
            name: partDetails.description || partDetails.code,
            path: deviceManualPath,
          });
        }
      });
    });

    return rows;
  }, [sadlSet, setLabel]);

  return manualRows;
};
